import { Controller } from "@hotwired/stimulus"

// Manages a tag/chip list of file paths for RMM tool approval.
// Each path is rendered as a removable chip with a hidden input.
//
// Two modes:
//   editing=true  — remove buttons visible, add input visible, Save/Cancel shown
//   editing=false — read-only path list, "Edit Paths" button shown
//
// Unreviewed tools start in editing mode; approved tools start read-only.
export default class extends Controller {
  static targets = ["list", "input", "template", "editButton", "editControls"]
  static values = { editing: Boolean }

  connect() {
    this.#syncUI()
  }

  edit(event) {
    event.preventDefault()
    this.#snapshot = this.listTarget.innerHTML
    this.editingValue = true
  }

  cancel(event) {
    event.preventDefault()
    if (this.#snapshot != null) {
      this.listTarget.innerHTML = this.#snapshot
      this.#snapshot = null
    }
    this.editingValue = false
  }

  editingValueChanged() {
    this.#syncUI()
  }

  add(event) {
    event.preventDefault()
    const value = this.inputTarget.value.trim()
    if (value === "") return
    if (this.#pathExists(value)) {
      this.inputTarget.value = ""
      return
    }

    this.#appendChip(value)
    this.inputTarget.value = ""
  }

  addOnEnter(event) {
    if (event.key === "Enter") {
      this.add(event)
    }
  }

  remove(event) {
    event.preventDefault()
    event.stopPropagation()
    const chip = event.currentTarget.closest("[data-path-chip]")
    if (chip) chip.remove()
  }

  // --- private ---

  #snapshot = null

  #syncUI() {
    const editing = this.editingValue

    if (this.hasEditButtonTarget) {
      this.editButtonTarget.classList.toggle("hidden", editing)
    }
    if (this.hasEditControlsTarget) {
      this.editControlsTarget.classList.toggle("hidden", !editing)
    }

    this.listTarget.querySelectorAll("[data-remove-button]").forEach((btn) => {
      btn.classList.toggle("hidden", !editing)
    })
  }

  #pathExists(value) {
    const normalized = value.toLowerCase()
    const inputs = this.listTarget.querySelectorAll("input[name='approved_paths[]']")
    return Array.from(inputs).some((input) => input.value.toLowerCase() === normalized)
  }

  #appendChip(value) {
    const clone = this.templateTarget.content.firstElementChild.cloneNode(true)
    clone.querySelector("[data-role='label']").textContent = value
    clone.querySelector("input").value = value
    this.listTarget.appendChild(clone)
  }
}
